import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useSessionStore } from '@/features/auth/sessionStore'
import {
  ChatIcon,
  LocationIcon,
  PhoneIcon,
  PlayIcon,
  TelegramIcon,
} from '@/components/ui/icons'
import s from './site.module.css'

/* The one footer.

   OUTSTANDING: the phone number and social handles below are placeholders and
   need the real ones before launch. */

export function SiteFooter(): ReactNode {
  const status = useSessionStore((state) => state.status)
  const userType = useSessionStore((state) => state.user?.user_type)
  const isStudent = status === 'authenticated' && userType === 'student'
  const isAdmin = status === 'authenticated' && userType === 'admin'
  const isAnonymous = !isStudent && !isAdmin

  return (
    <footer className={s.footer}>
      <div className={s.footerInner}>
        <div className={s.footerBrand}>
          <div className={s.footerBrandRow}>
            <img src="/favicon.svg" alt="" className={s.brandMark} />
            <span className={s.footerBrandName}>Shantha Marga</span>
          </div>
          <p className={s.footerTagline}>
            Your structured path to examination excellence — bilingual test series built to mirror the
            real exam.
          </p>

          <div className={s.socialRow}>
            <a href="tel:+910000000000" className={[s.social, s.socialPhone].join(' ')} aria-label="Call us">
              <PhoneIcon />
            </a>
            <a
              href="https://t.me/"
              className={[s.social, s.socialTelegram].join(' ')}
              aria-label="Telegram"
              target="_blank"
              rel="noreferrer noopener"
            >
              <TelegramIcon />
            </a>
            <a
              href="https://wa.me/"
              className={[s.social, s.socialWhatsapp].join(' ')}
              aria-label="WhatsApp"
              target="_blank"
              rel="noreferrer noopener"
            >
              <ChatIcon />
            </a>
            <a
              href="https://youtube.com/"
              className={[s.social, s.socialYoutube].join(' ')}
              aria-label="YouTube"
              target="_blank"
              rel="noreferrer noopener"
            >
              <PlayIcon />
            </a>
            <a
              href="https://maps.google.com/"
              className={[s.social, s.socialMaps].join(' ')}
              aria-label="Find us on the map"
              target="_blank"
              rel="noreferrer noopener"
            >
              <LocationIcon />
            </a>
          </div>
        </div>

        <div className={s.footerCol}>
          <span className={s.footerColTitle}>Quick links</span>
          {/* Offering "Login" to someone already signed in reads as a broken
              session, so each audience gets only the links that apply to it.

              A share-link guest falls into the anonymous branch on purpose:
              Home, Register and Login are the only three of these that a guest
              token can open, and Register is exactly the offer their result
              screen has just made them. */}
          <nav className={s.footerLinks} aria-label="Quick links">
            <Link to="/">Home</Link>
            {isStudent && (
              <>
                <Link to="/dashboard">My test series</Link>
                <Link to="/profile">My profile</Link>
              </>
            )}
            {isAdmin && <Link to="/admin">Admin portal</Link>}
            {isAnonymous && (
              <>
                <Link to="/signup">Register</Link>
                <Link to="/login">Login</Link>
              </>
            )}
          </nav>
        </div>

        <div className={s.footerCol}>
          <span className={s.footerColTitle}>Legal</span>
          <nav className={s.footerLinks} aria-label="Legal">
            <Link to="/terms">Terms of service</Link>
            <Link to="/privacy">Privacy policy</Link>
          </nav>
        </div>

        <div className={s.footerCol}>
          <span className={s.footerColTitle}>Support</span>
          <nav className={s.footerLinks} aria-label="Support">
            <Link to="/contact">Contact support</Link>
            <Link to="/about">About us</Link>
          </nav>
        </div>
      </div>

      {/* Ownership on one side, authorship on the other — the same pairing the
          review PDF's footer carries, so the document and the screen it was
          generated from say the same thing. */}
      <div className={s.footerBottom}>
        <div className={s.footerBottomInner}>
          <span>© {new Date().getFullYear()} Shantha Marga Test Series. All rights reserved.</span>
          <a
            href="https://www.nexenza.com"
            className={s.creditLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span className={s.creditLabel}>Developed by</span>
            <span className={s.creditName}>Nexenza Technologies</span>
            <span className="visually-hidden">(opens in a new tab)</span>
          </a>
        </div>
      </div>
    </footer>
  )
}
